/**
 * Color math for company branding. A company picks its primary color, the platform derives the
 * variants for light and dark mode so that WCAG 2.2 AA holds no matter which color was chosen
 * (docs/ui/design-system.md, section "Theming").
 */

export const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

/** Page backgrounds the brand colors are checked against. Must match the surface tokens in @nexus/ui. */
export const BRAND_SURFACES = { light: '#ffffff', dark: '#15171c' } as const;

const WHITE = '#ffffff';
const INK = '#111318';

/** WCAG 1.4.3 (text) and 1.4.11 (focus rings, borders, active indicators). */
export const MIN_TEXT_CONTRAST = 4.5;
export const MIN_UI_CONTRAST = 3;

type Rgb = [number, number, number];
interface Oklch {
  l: number;
  c: number;
  h: number;
}

function parseHex(hex: string): Rgb {
  if (!HEX_COLOR_PATTERN.test(hex)) throw new Error(`Invalid color: ${hex}`);
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255) as Rgb;
}

function toHex(rgb: Rgb): string {
  return `#${rgb
    .map((c) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const fromLinear = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex).map(toLinear) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [
    number,
    number,
  ];
  return (hi + 0.05) / (lo + 0.05);
}

// OKLab conversion after Björn Ottosson, used to change lightness without shifting the hue.
function toOklch(hex: string): Oklch {
  const [r, g, b] = parseHex(hex).map(toLinear) as Rgb;
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return { l: L, c: Math.hypot(A, B), h: Math.atan2(B, A) };
}

function oklchToRgb({ l: L, c, h }: Oklch): Rgb {
  const A = c * Math.cos(h);
  const B = c * Math.sin(h);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ].map(fromLinear) as Rgb;
}

function fromOklch(color: Oklch): string {
  // Reduce chroma until the color fits into sRGB.
  let { c } = color;
  for (;;) {
    const rgb = oklchToRgb({ ...color, c });
    if (rgb.every((v) => v >= -0.0005 && v <= 1.0005) || c < 0.001) {
      return toHex(rgb.map((v) => Math.min(1, Math.max(0, v))) as Rgb);
    }
    c *= 0.95;
  }
}

/**
 * Moves the lightness of `hex` away from `against` until the contrast reaches `min`.
 * Returns the original color when it already passes.
 */
export function adjustForContrast(hex: string, against: string, min: number): string {
  if (contrastRatio(hex, against) >= min) return hex;
  const base = toOklch(hex);
  const step = relativeLuminance(against) > 0.5 ? -0.01 : 0.01;
  for (let l = base.l; l >= 0 && l <= 1; l += step) {
    const candidate = fromOklch({ ...base, l });
    if (contrastRatio(candidate, against) >= min) return candidate;
  }
  return step < 0 ? INK : WHITE;
}

/** Text color (white or ink) with the higher contrast on the given background. */
export function readableTextOn(background: string): string {
  return contrastRatio(WHITE, background) >= contrastRatio(INK, background) ? WHITE : INK;
}

export interface BrandColors {
  /** Indicators: focus ring, active navigation, selected state. At least 3:1 on the surface. */
  primary: string;
  /** Text and icons in the brand color. At least 4.5:1 on the surface. */
  primaryText: string;
  /** Filled primary buttons. */
  primarySolid: string;
  /** Text on `primarySolid`, at least 4.5:1. */
  primarySolidForeground: string;
}

export interface BrandPalette {
  light: BrandColors;
  dark: BrandColors;
  /** True when a variant had to be changed to reach the contrast minimum. */
  adjusted: boolean;
}

/**
 * Filled buttons keep white text when the color is close to passing (e.g. a saturated orange gets
 * slightly darker). Light colors keep their tone and get dark text instead.
 */
function solidFor(primary: string): string {
  const white = contrastRatio(WHITE, primary);
  if (white >= MIN_TEXT_CONTRAST || white < MIN_UI_CONTRAST) return primary;
  return adjustForContrast(primary, WHITE, MIN_TEXT_CONTRAST);
}

function deriveMode(primary: string, surface: string): BrandColors {
  const solid = solidFor(primary);
  return {
    primary: adjustForContrast(primary, surface, MIN_UI_CONTRAST),
    primaryText: adjustForContrast(primary, surface, MIN_TEXT_CONTRAST),
    primarySolid: solid,
    primarySolidForeground: readableTextOn(solid),
  };
}

export function deriveBrandPalette(primary: string): BrandPalette {
  const light = deriveMode(primary, BRAND_SURFACES.light);
  const dark = deriveMode(primary, BRAND_SURFACES.dark);
  const adjusted = [light, dark]
    .flatMap((mode) => [mode.primary, mode.primaryText, mode.primarySolid])
    .some((c) => c.toLowerCase() !== primary.toLowerCase());
  return { light, dark, adjusted };
}
